import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {formatInteger, trackEvent} from '@unifocus/js-utils';
import { HorizontalPanel } from '../../layout/horizontalpanel';
import { Toolbar } from '../toolbar';
import { PaginationProps } from './Pagination';
import { SimplePaginationButtons } from './SimplePaginationButtons';
import { Text } from '../../display/text';

export interface SimplePaginationProps extends Omit<PaginationProps, 'simple'> {

}

export const SimplePagination = (props: SimplePaginationProps) => {

   const { style: styleProp, size, current, defaultCurrent, total, pageSize, onChange, disabled, analyticsName } = props;

   const [curPage, setCurPage ] = useState<number>(current || defaultCurrent || 1);

   const curPageSize = pageSize || 10;
   const curPageTotal = Math.floor((total || 0) / curPageSize);

   const [leftPagerDisabled, setLeftPagerDisabled] = useState<boolean>(curPage <= 1);
   const [rightPagerDisabled, setRightPagerDisabled] = useState<boolean>(curPage >= curPageTotal);

   useEffect(() => {
      setCurPage(current || defaultCurrent || 1);
   }, [current, defaultCurrent]);

   useEffect(()=>{
      setLeftPagerDisabled(curPage<=1);
      setRightPagerDisabled(curPage >= curPageTotal);
   }, [curPage, curPageTotal]);

   const gotoPreviousPage = useCallback(() => {
      if (current) {
         if (current > 1) {
            if (onChange) {
               onChange(current-1, curPageSize);
            }
            if (analyticsName) {
               trackEvent("Navigation",
                          "Page Previous",
                          analyticsName + " " + (current - 1) + "/" + curPageTotal);
            }
         }
      }
      else {
         setCurPage((page: number) => {
            if (page <= 1) {
               return page;
            }
            setTimeout(()=>{
               if (onChange) {
                  onChange(page - 1, curPageSize);
               }
            }, 0);

            if (analyticsName) {
               trackEvent("Navigation",
                          "Page Previous",
                          analyticsName + " " + (page - 1) + "/" + curPageTotal);
            }

            return --page;
         });
      }
   },[analyticsName, curPageSize, current, onChange, curPageTotal]);

   const gotoNextPage = useCallback(()=>{
      if (current) {
         if (current < curPageTotal) {
            if (onChange) {
               onChange(current+1, curPageSize);
            }
            if (analyticsName) {
               trackEvent("Navigation",
                          "Page Next",
                          analyticsName + " " + (current + 1) + "/" + curPageTotal);
            }
         }
      }
      else {
         setCurPage((page: number) => {
            if (page >= curPageTotal) {
               return page;
            }
            setTimeout(()=>{
               if (onChange) {
                  onChange(page + 1, curPageSize);
               }
            }, 0);

            if (analyticsName) {
               trackEvent("Navigation",
                          "Page Next",
                          analyticsName + " " + (page + 1) + "/" + curPageTotal);
            }

            return ++page;
         });
      }
   },[curPageTotal, analyticsName, curPageSize, current, onChange]);

   const gotoFirstPage = useCallback(()=>{
      if (current) {
         if (current !== 1) {
            if (onChange) {
               onChange(1, curPageSize);
            }
            if (analyticsName) {
               trackEvent("Navigation",
                          "Page First",
                          analyticsName + " " + (1 + "/" + curPageTotal));
            }
         }
      }
      else {
         setCurPage(1);
         if (onChange) {
            onChange(1, curPageSize);
         }
         if (analyticsName) {
            trackEvent("Navigation",
                       "Page First",
                       analyticsName + " " + (1 + "/" + curPageTotal));
         }
      }
   },[analyticsName, current, onChange, curPageSize, curPageTotal]);

   const gotoLastPage = useCallback(()=>{
      if (current) {
         if (curPageTotal > 0 && current !== curPageTotal) {
            if (onChange) {
               onChange(curPageTotal, curPageSize);
            }
            if (analyticsName) {
               trackEvent("Navigation",
                  "Page Last",
                          analyticsName + " " + curPageTotal + "/" + curPageTotal);
            }
         }
      }
      else {
         if (curPageTotal > 0) {
            setCurPage(curPageTotal);
            if (onChange) {
               onChange(curPageTotal, curPageSize);
            }
            if (analyticsName) {
               trackEvent("Navigation",
                          "Page Last",
                          analyticsName + " " + curPageTotal + "/" + curPageTotal);
            }
         }
      }
   },[curPageTotal, analyticsName, current, onChange, curPageSize]);

   const leftPager = useMemo(()=>{
      return (<SimplePaginationButtons direction={'left'} size={size} onPageClick={gotoPreviousPage} onLimitClick={gotoFirstPage} disabled={disabled || leftPagerDisabled}/>);
   },[size, gotoPreviousPage, gotoFirstPage, leftPagerDisabled, disabled]);

   const currentPageDesignator = useMemo(()=>{
      return (<Text>{formatInteger(curPage)}&nbsp;/&nbsp;{formatInteger(curPageTotal)}</Text>);
   },[curPage, curPageTotal]);

   const rightPager = useMemo(()=>{
      return (<SimplePaginationButtons direction={'right'} size={size} onPageClick={gotoNextPage} onLimitClick={gotoLastPage} disabled={disabled || rightPagerDisabled}/>);
   },[size, gotoNextPage, gotoLastPage, rightPagerDisabled, disabled]);

   const style = useMemo(()=>({
      ...styleProp
   }), [styleProp]);

   return (
      <HorizontalPanel align={'center'}>
         <Toolbar
            leftContent={leftPager}
            middleContent={currentPageDesignator}
            rightContent={rightPager}
            className={`uf-simple-pagination ${props.className || ''}`}
            style={style}
         />
      </HorizontalPanel>
   );
};

